/** URL <-> params mapping + human search summary (User-Brief 22.09.2026). */

/** Structured location entity currently held by the params (or null). */
export function locationFromParams(params) {
  if (!params) return null;
  if (params.postal_code) {
    return { type: "postal_code", value: params.postal_code, label: params.location_label || params.postal_code };
  }
  if (params.region) {
    return { type: "region", value: params.region, label: params.location_label || params.region };
  }
  if (params.city) {
    return { type: "city", value: params.city, label: params.location_label || params.city };
  }
  return null;
}

/** Patch that sets EXACTLY one canonical location key (or clears all). */
export function locationPatch(loc) {
  const patch = { city: null, postal_code: null, region: null, location_label: null };
  if (loc) {
    patch[
      loc.type === "region" ? "region"
        : loc.type === "postal_code" ? "postal_code"
          // city AND district both travel in `city` — the backend
          // resolves both layers with the same canonical resolver.
          : "city"
    ] = loc.value;
    patch.location_label = loc.label || loc.value;
  }
  return patch;
}

const NUM_KEYS = new Set([
  "price_min", "price_max", "year_min", "year_max", "mileage_min",
  "mileage_max", "power_min", "power_max", "rooms_min", "rooms_max",
  "area_min", "area_max", "year_built_min", "year_built_max", "yield_min",
  "seats_min", "doors_min", "owners_max", "new_days",
]);
const BOOL_KEYS = new Set(["price_reduced", "accident_free", "rated"]);
const SKIP_KEYS = new Set(["location_label"]);

export function paramsToQuery(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "" || v === false) return;
    sp.set(k, String(v));
  });
  return sp.toString();
}

export function queryToParams(search) {
  const sp = new URLSearchParams(search || "");
  const out = {};
  sp.forEach((v, k) => {
    if (NUM_KEYS.has(k)) {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = n;
    } else if (BOOL_KEYS.has(k)) {
      out[k] = v === "true";
    } else {
      out[k] = v;
    }
  });
  return out;
}

export function summarizeSearch(vertical, params, t) {
  const bits = [];
  if (vertical === "auto") {
    if (params.make) bits.push(String(params.make).split(",").join(", "));
    if (params.model) bits.push(String(params.model).split(",").join(", "));
  } else {
    bits.push(params.transaction_type === "rent" ? t("hero.rentTx") : t("hero.buy"));
    if (params.object_type) {
      bits.push(t(`enums:object_type.${params.object_type}`, { defaultValue: params.object_type }));
    }
  }
  const loc = params.location_label || params.city || params.postal_code || params.region;
  if (loc) bits.push(loc);
  else if (params.country) bits.push(params.country);
  else bits.push(t("country.all"));
  if (params.price_max) bits.push(`${t("search.upTo")} ${Math.round(params.price_max).toLocaleString("de-AT")} €`);
  if (bits.length === 0) bits.push(t(vertical === "auto" ? "hero.cars" : "hero.realEstate"));
  return bits.slice(0, 4).join(" · ");
}

/** Chip list of ACTIVE criteria (for results header + empty state). */
export function activeCriteria(vertical, params, t) {
  const fmtEur = (n) => `${Math.round(n).toLocaleString("de-AT")} €`;
  const out = [];
  const push = (key, label) => out.push({ key, label });
  if (params.make) push("make", String(params.make).split(",").join(", "));
  if (params.model) push("model", String(params.model).split(",").join(", "));
  if (params.object_type) {
    push("object_type", t(`enums:object_type.${params.object_type}`, { defaultValue: params.object_type }));
  }
  const loc = params.location_label || params.city || params.postal_code || params.region;
  if (loc) push("__location", loc);
  if (params.country) push("country", params.country);
  if (params.price_min) push("price_min", `≥ ${fmtEur(params.price_min)}`);
  if (params.price_max) push("price_max", `≤ ${fmtEur(params.price_max)}`);
  if (params.year_min) push("year_min", `${t("hero.yearFrom")} ${params.year_min}`);
  if (params.year_max) push("year_max", `${t("search.yearTo")} ${params.year_max}`);
  if (params.mileage_max) push("mileage_max", `≤ ${params.mileage_max.toLocaleString("de-AT")} km`);
  if (params.mileage_min) push("mileage_min", `≥ ${params.mileage_min.toLocaleString("de-AT")} km`);
  if (params.power_min) push("power_min", `≥ ${params.power_min} kW`);
  if (params.power_max) push("power_max", `≤ ${params.power_max} kW`);
  if (params.fuel) push("fuel", t(`enums:fuel.${params.fuel}`, { defaultValue: params.fuel }));
  if (params.gearbox) push("gearbox", t(`enums:gearbox.${params.gearbox}`, { defaultValue: params.gearbox }));
  if (params.body_type) push("body_type", t(`enums:body_type.${params.body_type}`, { defaultValue: params.body_type }));
  if (params.color) push("color", String(params.color).split(",").join(", "));
  if (params.seats_min) push("seats_min", `≥ ${params.seats_min} ${t("search.seats")}`);
  if (params.doors_min) push("doors_min", `≥ ${params.doors_min} ${t("search.doors")}`);
  if (params.owners_max) push("owners_max", `≤ ${params.owners_max} ${t("search.owners")}`);
  if (params.accident_free) push("accident_free", t("search.accidentFree"));
  if (params.rooms_min) push("rooms_min", `≥ ${params.rooms_min} ${t("card.roomsShort")}`);
  if (params.rooms_max) push("rooms_max", `≤ ${params.rooms_max} ${t("card.roomsShort")}`);
  if (params.area_min) push("area_min", `≥ ${params.area_min} m²`);
  if (params.area_max) push("area_max", `≤ ${params.area_max} m²`);
  if (params.year_built_min) push("year_built_min", `${t("search.yearBuiltFrom")} ${params.year_built_min}`);
  if (params.year_built_max) push("year_built_max", `${t("search.yearBuiltTo")} ${params.year_built_max}`);
  if (params.yield_min) push("yield_min", `≥ ${params.yield_min} %`);
  if (params.seller_type) push("seller_type", t(`hero.seller_${params.seller_type}`, { defaultValue: params.seller_type }));
  if (params.features) {
    String(params.features).split(",").forEach((f) => {
      out.push({ key: `feature:${f}`, label: t(`enums:features.${f}`, { defaultValue: f }) });
    });
  }
  if (params.price_reduced) push("price_reduced", t("quick.priceReduced"));
  if (params.new_days) push("new_days", t("quick.newDays", { count: params.new_days }));
  if (params.rated) push("rated", t("search.ratedOnly"));
  if (params.quality === "deals") push("quality", t("quick.dealsOnly"));
  if (params.q) push("q", `„${params.q}“`);
  return out;
}

/** Remove one criterion (chip X) — returns new params. */
export function removeCriterion(params, key) {
  const next = { ...params };
  if (key === "__location") {
    delete next.city; delete next.postal_code; delete next.region; delete next.location_label;
  } else if (key.startsWith("feature:")) {
    const slug = key.slice(8);
    const rest = String(next.features || "").split(",").filter((f) => f && f !== slug);
    if (rest.length) next.features = rest.join(",");
    else delete next.features;
  } else if (key === "quality") {
    next.quality = "all";
  } else {
    delete next[key];
  }
  return next;
}
